import {
  Insurer,
  InsurerMetaDef,
  Partners,
} from '@lockton-apps/shared/interfaces';
import { APPLICATION, Application } from './application';
import { InsurerMetaService } from './insurer-meta.service';
import { InjectionToken } from '@angular/core';
import { AccaClassOfBusinessId, AccaOverClassOfBusinessId } from './cob-meta';

export const INSURER_META_DATA = new InjectionToken<InsurerMetaDef>(
  'Current application insurers meta'
);

const INSURER_META_DATA_DEFAULT: InsurerMetaDef = {
  [Partners.ACCA]: {
    DEFAULT: {},
    [AccaOverClassOfBusinessId.NationalAccountantsAcca]: {
      [Insurer.Arch]: {
        id: Insurer.Arch,
        name: 'Arch Insurance (UK) Ltd',
        description: $localize`ACCA Members Scheme is underwritten by Arch Insurance (UK) Ltd for all sections apart from legal expenses which is provided by ARAG plc who underwrite for and on behalf of HDI Global Specialty SE. The Scheme is administered by Lockton Companies LLP. Arch Insurance (UK) Ltd is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority. ARAG plc is authorised, and regulated by the Financial Conduct Authority.`,
        website: '',
        policyWording: 'Lockton ACCA DEC2023',
        limitOfIndemnityBasis: $localize`Any one Claim or loss including Claimants’ costs and expenses, Defence Costs in addition, but limited to in the aggregate for the Period of Insurance, including Defence Costs, in respect of Claims or losses arising out of a Cyber Act, which sum shall be included in and is not additional to the Limit of Indemnity for any one Claim or loss.`,
        excessBasis: $localize`Each and every claim excluding defence costs and subject to an aggregated cap of three times the excess for the total of all excesses. Note on occasion for high risk work Arch may specify that the aggregated cap should not apply.`,
        legalExpenses: {
          insurer: $localize`ARAG plc for & on behalf of HDI Global Specialty SE`,
          limitOfIndemnity: `£250,000`,
          limitOfIndemnityBasis: $localize`£250,000 for items 1 Employment to 10 Personal Injury (inclusive) and 12 Contract and Debt Recovery\n£25,000 for item 11 Executive Suite and 13 Crisis Communication\n£1,000,000 aggregate limit under insured event 2 Employment Compensation Awards`,
          excess: $localize`£Nil`,
        },
      },
      [Insurer.Allianz]: {
        id: Insurer.Allianz,
        name: 'Allianz Insurance plc',
        description: $localize`ACCA Members Scheme is underwritten by Allianz Insurance plc. Registered in England number 84638. Registered office. 57 Ladymead, Guildford, Surrey, GU1 1DB, United Kingdom.\nAllianz Insurance plc is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority and the Prudential Regulation Authority. Financial Services Register number 121849.`,
        website: 'www.allianz.co.uk',
        policyWording: 'Lockton ACCA DEC2024v1',
        limitOfIndemnityBasis: $localize`Any one Claim or loss including Claimants’ costs and expenses, Defence Costs in addition, but limited to in the aggregate for the Period of Insurance, including Defence Costs, in respect of Claims or losses arising out of a Cyber Act, which sum shall be included in and is not additional to the Limit of Indemnity for any one Claim or loss.`,
        excessBasis: $localize`Each and every Claim, excluding Defence Costs capped at £=({excess} * 3 | eval) in the annual aggregate.\nLoss of Documents, Disciplinary Hearing, Reputation Indemnity and Compensation for Court Attendance This excess is 10% of any amount payable or £500 each and every Claim whichever is the higher.`,
      },
    },
    [AccaClassOfBusinessId.EmployersLiability]: {
      [Insurer.Arch]: {
        id: Insurer.Arch,
        name: 'Arch Insurance (UK) Ltd',
        excess: $localize`£Nil`,
        limitOfIndemnityBasis: $localize`Each and every occurrence, costs and expenses are in addition`,
        territorialLimits: $localize`Anywhere within the limits of Great Britain, Northern Ireland, the Channel Islands and the Isle of Man and temporarily engaged in the Business elsewhere in the world`,
      },
      [Insurer.Allianz]: {
        id: Insurer.Allianz,
        name: 'Allianz Insurance plc',
        excess: $localize`£Nil`,
        limitOfIndemnityBasis: $localize`Each and every occurrence, costs and expenses are in addition`,
        territorialLimits: $localize`Anywhere within the limits of Great Britain, Northern Ireland, the Channel Islands and the Isle of Man and temporarily engaged in the Business elsewhere in the world`,
      },
    },
    [AccaClassOfBusinessId.PublicAndProductLiability]: {
      [Insurer.Arch]: {
        id: Insurer.Arch,
        name: 'Arch Insurance (UK) Ltd',
        excess: $localize`£250 Each and every Claim or loss for property damage only`,
        limitOfIndemnityBasis: $localize`Public Liability: Any claimant or number of claimants in respect of any one occurrence or all occurrences of a series arising out of one original cause, costs and expenses are in addition.\nProducts Liability: Each and every occurrence and in the aggregate including costs and expenses.`,
        territorialLimits: $localize`Anywhere within the limits of Great Britain, Northern Ireland, the Channel Islands or the Isle of Man; and manual and non-manual work carried out during temporary visits anywhere in the world (other than the United States of America or Canada).`,
      },
      [Insurer.Allianz]: {
        id: Insurer.Allianz,
        name: 'Allianz Insurance plc',
        excess: $localize`£250 Each and every Claim or loss for property damage only`,
        limitOfIndemnityBasis: $localize`Public Liability: Any claimant or number of claimants in respect of any one occurrence or all occurrences of a series arising out of one original cause, costs and expenses are in addition (other than all claims made within the legal jurisdiction of United States of America or Canada where the Limit of Indemnity is inclusive of all costs and expenses).\nProducts Liability: Each and every occurrence and in the aggregate including costs and expenses`,
        territorialLimits: $localize`Public Liability: Anywhere within the limits of Great Britain, Northern Ireland, the Channel Islands or the Isle of Man; and manual and non-manual work carried out during temporary visits anywhere in the world (other than the United States of America or Canada).\nProducts Liability: Anywhere in the world in respect of Products supplied in or from Great Britain, Northern Ireland, the Channel Islands or the Isle of Man.`,
      },
    },
    [AccaClassOfBusinessId.GroupPersonalAccident]: {
      [Insurer.Arch]: {
        id: Insurer.Arch,
        name: 'Arch Insurance (UK) Ltd',
        limitOfIndemnityBasis: $localize`Any one claim and in the aggregate per Insured Person`,
        territorialLimits: $localize`Great Britain, Northern Ireland, Channel Islands or the Isle of Man`,
      },
      [Insurer.Allianz]: {
        id: Insurer.Allianz,
        name: 'Allianz Insurance plc',
        excess: $localize`£Nil`,
        limitOfIndemnityBasis: $localize`Any one Claim and in the aggregate per Insured Person`,
        territorialLimits: $localize`Great Britain, Northern Ireland, Channel Islands or the Isle of Man`,
      },
    },
    [AccaClassOfBusinessId.ManagementLiability]: {
      [Insurer.Arch]: {
        id: Insurer.Arch,
        name: 'Arch Insurance (UK) Ltd',
        excess: $localize`£Nil`,
        limitOfIndemnityBasis: $localize`Any one Claim, including Defence Costs`,
      },
      [Insurer.Allianz]: {
        id: Insurer.Allianz,
        name: 'Allianz Insurance plc',
        excess: $localize`£Nil`,
        limitOfIndemnityBasis: $localize`Any one Claim, including Defence Costs`,
      },
    },
  },
} as unknown as InsurerMetaDef;

export function provideInsurerMeta() {
  return [
    InsurerMetaService,
    {
      provide: INSURER_META_DATA,
      useFactory: (application: Application) => {
        switch (application) {
          case Application.ClientGateway:
            return INSURER_META_DATA_DEFAULT;

          case Application.Latam:
            return {};
        }
      },
      deps: [APPLICATION],
    },
  ];
}
